import FJTextContent from '../../core/textBasedEditing/FJTextContent.js';
import FJText from '../../core/textBasedEditing/FJText.js';
import FJUndoRedoManager from '../../core/FJUndoRedoManager.js';
import FJStringUtil from '../../util/FJStringUtil.js';
import { franc } from 'franc-min';

const undoRedoManager = new FJUndoRedoManager();

let _dataInfo;
let _dataTextMap;
let _media = null;
let _scrollerRect = null;

const _findAllMatchIndices = (text, pattern) => {
    const regex = new RegExp(pattern, 'g');
    const matches = [...text.matchAll(regex)];
    return matches.map(match => match.index);
};

const FJTextEditStore = {
    loadDataInfo: (dataInfo, duration = 0, language) => {
        _dataTextMap = null;
        _dataInfo = dataInfo;
        const isSplit = ['chinese', 'japanese'].indexOf(language) !== -1;
        dataInfo.chunks.forEach((chunk, index) => {
            const { text = '', timestamp = [] } = chunk;
            const startTime = timestamp[0];
            const endTime = timestamp[1];
            if (index === 0 && startTime >= 0.2) {
                const empty = new FJText({ text: '', startTime: 0, endTime: startTime });
                FJTextContent.addText(empty);
            }

            // 分离多字的连词
            if (isSplit && text && text.trim().length >= 2) {
                const targetLanguage = franc(text, { only: ['cmn', 'eng', 'jpn', 'kor'], minLength: 2 });
                if (targetLanguage === 'cmn' || targetLanguage === 'jpn' || targetLanguage === 'kor') {
                    const d = endTime - startTime;
                    const sd = d / text.length;
                    let realStartTime = startTime;
                    let realEndTime = startTime;
                    let tt = '';
                    for (let i = 0; i < text.length; i++) {
                        const t = text[i];
                        realEndTime += sd;
                        if (t === ' ') {
                            tt += t;
                        } else {
                            const textGraph = new FJText({
                                text: tt ? tt : t,
                                startTime: realStartTime,
                                endTime: realEndTime,
                            });
                            FJTextContent.addText(textGraph);
                            tt = '';
                            realStartTime = realEndTime;
                        }
                    }
                } else {
                    const textGraph = new FJText({ text: text, startTime: startTime, endTime: endTime });
                    FJTextContent.addText(textGraph);
                }
            } else {
                const textGraph = new FJText({ text: text, startTime: startTime, endTime: endTime });
                FJTextContent.addText(textGraph);
            }

            if (index === dataInfo.chunks.length - 1 && duration - endTime >= 0.2) {
                const empty = new FJText({ text: '', startTime: endTime, endTime: duration });
                FJTextContent.addText(empty);
            }
        });
    },
    getOriginalDataInfo: () => {
        return _dataInfo;
    },

    //#region 获取media对象
    setMedia: media => {
        if (_media) _media.release();
        _media = media;
    },
    getMedia: () => {
        return _media;
    },
    //#endregion

    // 获取文字编辑区域的rect
    getTextEditPreviewBoxRect: id => {
        const div = document.getElementById(id);
        if (!div) return { x: 0, y: 0 };
        if (!_scrollerRect) {
            _scrollerRect = div.getBoundingClientRect();
        }
        return _scrollerRect;
    },

    //#region 鼠标事件相关
    selectAtPoint: point => {
        FJTextContent.selectAtPoint(point);
        FJTextContent.renderStyle();
    },
    selectLineAtPoint: point => {
        FJTextContent.selectAtPoint(point);
        FJTextContent.selectLine();
        FJTextContent.renderStyle();
    },
    selectInRect: (rect, startPoint, endPoint) => {
        const textList = FJTextContent.getAllTextList();

        const __findFromIndex = () => {
            let fromIndex = -1;
            // 头满足要求：文字的maxY > rect.y && maxX > rect.x
            for (let i = 0; i < textList.length; i++) {
                const item = textList[i];
                if (item.isHide) continue;
                const graphRect = item.getRect();
                if (graphRect.y + graphRect.height >= rect.y && graphRect.x + graphRect.width >= rect.x) {
                    fromIndex = i;
                    break;
                }
            }

            return fromIndex;
        };
        const __findEndIndex = () => {
            let endIndex = -1;
            for (let i = textList.length - 1; i >= 0; i--) {
                const item = textList[i];
                if (item.isHide) continue;
                const graphRect = item.getRect();
                if (graphRect.x <= rect.x + rect.width && graphRect.y <= rect.y + rect.height) {
                    endIndex = i;
                    break;
                }
            }
            return endIndex;
        };
        // 找到头尾
        // 头满足要求：文字的maxY > rect.y && maxX > rect.x
        let fromIndex = __findFromIndex();
        let endIndex = __findEndIndex();

        if (fromIndex === -1 || endIndex === -1) {
            FJTextContent.deSelectAll();
        } else if (fromIndex === endIndex) {
            FJTextContent.selectIndex(fromIndex);
        } else {
            const fText = textList[fromIndex];
            const eText = textList[endIndex];
            const fRect = fText.getRect();
            const eRect = eText.getRect();
            const isSameLine = fRect.y === eRect.y;
            if (!isSameLine) {
                for (let i = fromIndex; i <= endIndex; i++) {
                    const t = textList[i];
                    const rect = t.getRect();
                    if (rect.x + rect.width >= startPoint.x) {
                        fromIndex = i;
                        break;
                    }
                }
                for (let i = endIndex; i >= fromIndex; i--) {
                    const t = textList[i];
                    const rect = t.getRect();
                    if (rect.x <= endPoint.x) {
                        endIndex = i;
                        break;
                    }
                }
            }

            FJTextContent.selectFromIndexToIndex(fromIndex, endIndex);
        }
        FJTextContent.renderStyle();
    },
    //#endregion

    //#region 设置选中对象，获取选择对象的索引
    selectIndex: index => {
        FJTextContent.selectIndex(index);
        FJTextContent.renderStyle();
    },

    //#endregion

    matchText: text => {
        const allText = _dataInfo.text.toLowerCase();
        const textList = FJTextContent.getAllTextList();
        // 清空匹配
        textList.forEach(textGraph => {
            textGraph.isSelected = false;
            textGraph.isMatch = false;
        });

        if (!text) {
            FJTextContent.renderStyle();
            return [];
        }

        const textIndexMap = FJTextEditStore.getDataTextMap();
        const indices = _findAllMatchIndices(allText, text.toLowerCase());
        if (indices.length !== 0) {
            const matchTextLength = text.length;
            indices.forEach((allTextIndex, tIndex) => {
                const matchIndex = allTextIndex;
                const isFirst = tIndex === 0;
                const startIndex = textIndexMap.findIndex(d => matchIndex >= d.startIndex && matchIndex < d.endIndex);
                const nextIndex = matchIndex + matchTextLength - 1;
                const endIndex = textIndexMap.findIndex(d => nextIndex >= d.startIndex && nextIndex < d.endIndex);
                for (let i = startIndex; i <= endIndex; i++) {
                    const textData = textList[i];
                    textData.isMatch = !textData.isHide;
                    if (isFirst && textData.isMatch) textData.isSelected = true;
                }
            });
        }

        // FJTextContent.matchToText(text);
        const matchList = FJTextContent.getMatchTextList();
        // const firstItem = matchList.find(item => !item.isHide);
        // if (firstItem) firstItem.isSelected = true;
        FJTextContent.renderStyle();
        return matchList;
    },
    checkCanBeDelete: () => {
        const textList = FJTextContent.getSelectedTextList();
        const __setDelete = value => {
            textList.forEach(item => {
                item.isDelete = value;
            });
        };

        __setDelete(true);

        let isVain = false;
        const timeDataList = FJTextEditStore.getTimeDataForAllText();
        if (!timeDataList) {
            __setDelete(false);
            return false;
        }
        for (let i = 0; i < timeDataList.length; i++) {
            const time = timeDataList[i];
            if (time.endTime - time.startTime < 0.1) {
                isVain = true;
                break;
            }
        }
        if (!isVain) {
            __setDelete(false);
        }
        return !isVain;
    },
    deleteSelectedText: (isHide = false) => {
        FJTextEditStore.addUndo();
        const textList = FJTextContent.getSelectedTextList();
        textList.forEach(item => {
            item.isDelete = true;
            if (isHide) {
                item.isHide = true;
                item.isSelected = false;
                item.isMatch = false;
                item.destroy();
            }
        });

        // 如果隐藏删除的文字，则后续的文字的rect需要更新，需要清空后续文字的rect
        if (isHide && textList.length !== 0) {
            const lastItem = textList[textList.length - 1];
            const index = FJTextContent.indexOfTextGraph(lastItem);
            const allList = FJTextContent.getAllTextList();
            for (let i = index + 1; i < allList.length; i++) {
                allList[i].clearRect();
            }
        }

        FJTextContent.renderStyle();
    },
    restoreSelectedText: () => {
        FJTextEditStore.addUndo();
        const textList = FJTextContent.getSelectedTextList();
        textList.forEach(item => {
            item.isDelete = false;
        });
        FJTextContent.renderStyle();
    },
    deleteMarkText: (isHide = false) => {
        const textList = FJTextContent.getAllTextList();
        const __check = () => {
            // 找到有标记的，但是没有删除的字段
            return textList.find(item => item.isMark && !item.isDelete);
        };

        if (!__check()) return;

        FJTextEditStore.addUndo();

        let needClearRect = false;
        textList.forEach(item => {
            if (item.isMark) {
                item.isDelete = true;
                if (isHide) {
                    needClearRect = true;
                    item.isHide = true;
                    item.isSelected = false;
                    item.isMatch = false;
                    item.destroy();
                }
            }
            if (needClearRect) item.clearRect();
        });
        FJTextContent.renderStyle();
    },
    downloadText: () => {
        const list = FJTextContent.getAllTextList();
        const showList = list.filter(item => !item.isDelete);
        let textContent = '';
        showList.forEach(item => {
            textContent += item.text;
            if (item.isParagraphEnd()) textContent += '\n';
        });

        // 创建 Blob 对象
        const blob = new Blob([textContent], { type: 'text/plain' });

        // 创建下载链接
        const url = URL.createObjectURL(blob);

        // 创建 <a> 标签并触发下载
        const a = document.createElement('a');
        a.href = url;
        a.download = FJStringUtil.getUseFileName(undefined, '.txt'); // 设置下载文件的名称
        document.body.appendChild(a);
        a.click();

        // 清理
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },
    getTimeDataForSelectedText: () => {
        const list = FJTextContent.getSelectedTextList();
        if (list.length > 0) {
            const part = [];
            let times = [];
            list.forEach((item, index) => {
                if (!item.isDelete) times.push(item);

                if (item.isDelete || index === list.length - 1) {
                    if (times.length > 0) {
                        part.push(times);
                    }
                    times = [];
                }
            });

            return part.length > 0
                ? part.map(items => {
                      return {
                          startTime: items[0].startTime,
                          endTime: items[items.length - 1].endTime,
                      };
                  })
                : null;
        }
        return null;
    },
    getTimeDataForAllText: () => {
        const list = FJTextContent.getAllTextList();
        if (list.length > 0) {
            const timePart = [];
            let times = [];
            list.forEach((item, index) => {
                if (!item.isDelete) times.push(item);

                if (item.isDelete || index === list.length - 1) {
                    if (times.length > 0) {
                        const startTime = times[0].startTime;
                        const endTime = times[times.length - 1].endTime;
                        timePart.push({ startTime, endTime });
                    }
                    times = [];
                }
            });
            return timePart.length > 0 ? timePart : null;
        }
        return null;
    },

    undo: (isAddRedo = true) => {
        isAddRedo && FJTextEditStore.addRedo();
        const state = undoRedoManager.undo();
        if (state) {
            FJTextContent.deSelectAll();
            FJTextContent.fromJson(state);
        }
    },
    redo: (isAddUndo = true) => {
        isAddUndo && FJTextEditStore.addUndo(true);
        const state = undoRedoManager.redo();
        if (state) {
            FJTextContent.deSelectAll();
            FJTextContent.fromJson(state);
        }
    },

    addUndo: (isRedo = false) => {
        undoRedoManager.addUndo(FJTextContent.toJson(), isRedo);
    },
    addRedo: () => {
        undoRedoManager.addRedo(FJTextContent.toJson());
    },

    hasUndo: () => {
        return undoRedoManager.hasUndo();
    },
    hasRedo: () => {
        return undoRedoManager.hasRedo();
    },
    clearUndoRedo: () => {
        undoRedoManager.clear();
    },

    // 根据时间进行高亮文字
    highlightFromTime: time => {
        let highlightItem = null;
        const list = FJTextContent.getAllTextList();
        list.forEach(item => {
            if (time >= item.startTime && time <= item.endTime) {
                item.renderHighLight('#efff00');
                highlightItem = item;
            } else {
                item.renderStyle();
            }
        });
        return highlightItem;
    },

    getDataTextMap: () => {
        if (_dataTextMap) return _dataTextMap;
        if (!_dataInfo) return null;
        const textList = FJTextContent.getAllTextList();
        let k = 0;
        _dataTextMap = textList.map(item => {
            if (item.isEmpty()) return { startIndex: -1, endIndex: -1 };
            else {
                const start = k;
                const end = k + item.text.length;
                k = end;
                return { startIndex: start, endIndex: end };
            }
        });
        return _dataTextMap;
    },

    // 匹配高光剪辑
    mathAIClippingContent: aiTextList => {
        const allText = _dataInfo.text.toLowerCase();
        const textList = FJTextContent.getAllTextList();
        // 先将所有的文字进行删除，再通过将匹配上的文字设置为未删除
        textList.forEach(item => {
            item.isSelected = false;
            item.isMatch = false;
            item.isMark = false;
            item.isDelete = true;
        });

        const textIndexMap = FJTextEditStore.getDataTextMap();

        // 重复的句子
        const repeatWord = {};

        for (let i = 0; i < aiTextList.length; i++) {
            const text = aiTextList[i].toLowerCase();
            const indices = _findAllMatchIndices(allText, text);
            let wordIndex = -1;
            if (indices.length === 1) {
                wordIndex = indices[0];
            } else if (indices.length > 1) {
                if (!repeatWord[text]) repeatWord[text] = { indices: indices, index: 0 };
                const data = repeatWord[text];
                const nextIndex = data.index + 1;
                // 判断下一个索引是否越界
                if (nextIndex < indices.length) {
                    wordIndex = indices[nextIndex];
                    data.index = nextIndex;
                }
            }

            if (wordIndex !== -1) {
                const index = textIndexMap.findIndex(d => wordIndex >= d.startIndex && wordIndex < d.endIndex);
                let matchText = '';
                for (let j = index; j < textList.length; j++) {
                    const textData = textList[j];
                    const t = j === index ? textData.text.replace(' ', '') : textData.text;
                    matchText += t;
                    if (matchText.length <= text.length) {
                        textData.isDelete = false;
                    }
                    if (matchText.length >= text.length) {
                        break;
                    }
                }
            } else {
                console.log('no match text', text);
            }
        }
    },
};

export default FJTextEditStore;
